import React, { useContext, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { useLocation, useNavigate } from "react-router-dom";

import { ClientForm } from "../../components/forms/ClientForm";
import { CompanyForm } from "../../components/forms/StoreForm";
import { DivisionForm } from "../../components/forms/DivisionForm";
import { TerminalForm } from "../../components/forms/TerminalForm";
import { CorporationForm } from "../../components/forms/CorporationForm";

import { Tree } from "../../components/tree/Tree";
import { TreeNodeModel, EntityType, Client, DeviceContract } from "../../components/tree/types";
import Message from "../../components/ui/Message";

import { ApplicationsContext } from "../../context/applications/ApplicationContext";
import { Application } from "../../models/interfaces";
import Loading from "../../components/ui/Loading";
import { AuthContext } from "../../context/auth";

export const ApplicationPage = () => {

  const location = useLocation();
  const application: Application | undefined = location.state?.application;

  const {
    isLoading,
    addApplication,
    updateApplication,
    getApplication,
    submitForApproval,
    loadApplications
  } = useContext(ApplicationsContext);

  const [tree, setTree] = useState<TreeNodeModel[]>([]);
  const [selected, setSelected] = useState<TreeNodeModel | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [isClientExistent, setIsClientExistent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useContext(AuthContext);
  const isUserPayfac = user.groups[0].is_payfac_group == 'Y';

  const navigate = useNavigate();

  /* ===========================
     INIT (NEW vs EDIT)
     =========================== */
  useEffect(() => {

    // ✨ EDITAR
    if (application) {

      loadExistingTree(application.APP_ID);

      return;
    }

    // ✨ NUEVO (comportamiento actual)
    const rootClient: TreeNodeModel = {
      id: uuid(),
      type: "CLIENT",
      name: "",
      saved: false,
      children: []
    };

    setTree([rootClient]);
    setSelected(rootClient);

  }, []);


  const loadExistingTree = async (appId: number) => {
    try {

      setIsLoadingTree(true);

      const treeFromApi = await getApplication(appId);

      const prepareTree = (node: TreeNodeModel): TreeNodeModel => ({
        ...node,
        originalEntityData: structuredClone(node.entityData),
        originalChildrenCount: node.children?.length ?? 0,
        children: node.children?.map(prepareTree) || []
      });

      const preparedTree = prepareTree(treeFromApi);

      preparedTree.entityId = appId.toString();

      setTree([preparedTree]);

      setIsClientExistent(preparedTree.isExistent);

      setSelected(preparedTree);

    } catch (e) {

      console.error(e);
      setErrorMessage("No fue posible cargar la aplicación.");

    } finally {

      setTimeout(() => {
        setIsLoadingTree(false);
      }, 500);

    }
  };

  /* ===========================
     HELPERS
     =========================== */

  const removeError = () => setErrorMessage('');
  const removeSuccessMessage = () => {
    setSuccessMessage('');
  }
  const removeConfirmMessage = () => setConfirmMessage('');

  const confirmSendToApprove = async () => {
    setConfirmMessage('');
    setIsSubmitting(true);

    const saved = await saveTreeBeforeApprove();

    if (!saved) {
      setIsSubmitting(false);
      return;
    }

    const result = await submitForApproval(tree[0].entityId);
    setIsSubmitting(false);
    if (result) {
      setSuccessMessage("Solicitud enviada correctamente.");
      if (isUserPayfac) {
        loadApplications(['DRAFT_PAYFAC']);
      } else {
        loadApplications(['DRAFT', 'REJECTED', 'PENDING_OFFICER']);
      }
      navigate(-1);
    } else {
      setErrorMessage(result)
    }
  }


  const addNode = (parent: TreeNodeModel) => {
    const nextType: Record<EntityType, EntityType | null> = {
      CLIENT: "CORP_CONTRACT",
      CORP_CONTRACT: "DIV_CONTRACT",
      DIV_CONTRACT: "STORE_CONTRACT",
      STORE_CONTRACT: "DEVICE_CONTRACT",
      DEVICE_CONTRACT: null,
    };

    const type = nextType[parent.type];
    if (!type) return;

    const newNode: TreeNodeModel = {
      id: uuid(),
      type,
      name: "",
      saved: false,
      children: []
    };

    parent.children.push(newNode);

    const newTree = [...tree];

    recalculateModified(newTree[0]);

    setTree(newTree);
    setSelected(newNode);
  };

  /* ===========================
     SAVE NODE
     =========================== */

  const saveNode = async (updated: TreeNodeModel) => {
    try {
      const newTree = updateNodeInTree(tree, {
        ...updated,
        saved: true
      });

      const root = newTree[0];
      let resp;
      let entityId;

      if (!root.entityId) {
        // 🆕 CREAR
        [resp, entityId] = await addApplication(root, true);

        if (resp !== "success") {
          setErrorMessage(resp);
          return;
        }

        newTree[0].entityId = entityId;
      } else {
        // ✏️ ACTUALIZAR
        resp = await updateApplication(root, root.entityId);



        if (resp !== "success") {
          setErrorMessage(resp);
          return;
        }
      }

      const updatedSelected = findNodeById(newTree, updated.id);

      setTree(newTree);
      setSelected(updatedSelected);
      setSuccessMessage("Información guardada correctamente.");

    } catch (e) {
      console.error(e);
      setErrorMessage("Error al guardar la información.");
    }
  };

  const findNodeById = (
    nodes: TreeNodeModel[],
    id: string
  ): TreeNodeModel | null => {

    for (const node of nodes) {

      if (node.id === id) {
        return node;
      }

      const found = findNodeById(node.children, id);

      if (found) {
        return found;
      }
    }

    return null;
  };

  const updateNodeLive = (updated: TreeNodeModel) => {

    const nodeToUpdate = {
      ...updated
    };

    const newTree = updateNodeInTree(tree, nodeToUpdate);

    recalculateModified(newTree[0]);

    setTree(newTree);



    if (selected?.id === nodeToUpdate.id) {
      setSelected(nodeToUpdate);
    }
  };


  const recalculateModified = (node: TreeNodeModel): boolean => {

    // Nodo nuevo
    if (node.isExistent !== true) {
      node.isModified = true;

      node.children?.forEach(recalculateModified);

      return true;
    }

    // ¿Cambió la estructura?
    const structureChanged =
      (node.children?.length ?? 0) !== (node.originalChildrenCount ?? 0);

    // ¿Cambiaron los datos del nodo?
    const selfChanged =
      structureChanged ||
      JSON.stringify(node.entityData) !==
      JSON.stringify(node.originalEntityData);

    // ¿Cambió algún hijo?
    const childrenChanged =
      node.children?.some(child => recalculateModified(child)) ?? false;

    node.isModified = selfChanged || childrenChanged;

    return node.isModified;
  };


  const saveTreeBeforeApprove = async () => {

    try {

      const root = tree[0];

      let resp;
      let entityId = root.entityId;

      // Actualizar árbol completo
      resp = await updateApplication(root, entityId);

      if (resp !== "success") {
        setErrorMessage(resp);
        return false;
      }

      const newTree = [...tree];



      setTree(newTree);

      return true;

    } catch (e) {
      console.error(e);
      setErrorMessage("Error guardando la solicitud.");
      return false;
    }
  };

  const updateNodeInTree = (
    nodes: TreeNodeModel[],
    updatedNode: TreeNodeModel
  ): TreeNodeModel[] =>
    nodes.map(node => {
      if (node.id === updatedNode.id) return updatedNode;
      if (node.children.length) {
        return { ...node, children: updateNodeInTree(node.children, updatedNode) };
      }
      return node;
    });

  const deleteNodeRecursive = (
    nodes: TreeNodeModel[],
    targetId: string
  ): TreeNodeModel[] =>
    nodes
      .filter(n => n.id !== targetId)
      .map(n => ({
        ...n,
        children: deleteNodeRecursive(n.children, targetId)
      }));

  const handleDelete = async (node: TreeNodeModel) => {

    const previousTree = tree;
    const updatedTree = deleteNodeRecursive(tree, node.id);


    setTree(updatedTree);

    const resp = await updateApplication(
      updatedTree[0],
      updatedTree[0].entityId
    );

    if (resp !== "success") {
      setErrorMessage(resp);
      setTree(previousTree); // rollback
    } else {
      const newTree = [...updatedTree];

      setTree([...newTree]);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleToApprove = () => {

    const isValid = validateTreeForApproval(tree);

    if (!isValid) return;
    setConfirmMessage("Confirma desea enviar esta solicitud?");
  };



  const hasInvalidLimits = (limits: any[] = []) => {
    return limits.some(limit => {
      const hasMaxAmount = limit.maxAmount !== undefined && limit.maxAmount !== "";
      const hasMaxSingleAmount = limit.maxSingleAmount !== undefined && limit.maxSingleAmount !== "";
      const hasMaxNumber = limit.maxNumber !== undefined && limit.maxNumber !== "";

      if (hasMaxAmount && Number(limit.maxAmount) < 0) return true;
      if (hasMaxSingleAmount && Number(limit.maxSingleAmount) < 0) return true;
      if (hasMaxNumber && Number(limit.maxNumber) < 0) return true;

      if (!hasMaxAmount && !hasMaxSingleAmount && !hasMaxNumber) return true;

      return false;
    });
  };

  const isDeviceValid = (node: TreeNodeModel) => {
    const data = node.entityData as DeviceContract;

    if (!data) return false;

    // campos base
    if (
      !data.product ||
      !data.contractName ||
      (!data.settlementParams?.localCurrencyAccountNumber && !data.settlementParams?.usdAccountNumber) ||
      (!isUserPayfac && !data.settlementParams?.tariffPlan)
    ) {
      return false;
    }

    // 🔥 límites
    if (data.adjustLimits === "Y" && hasInvalidLimits(data.deviceLimits)) {
      return false;
    }

    // 🔥 tarifas (si aplica)
    if (data.adjustTariffs === "Y") {

      const hasInvalidTariff = data.deviceTariffs?.some(t =>
        t.customize === true &&
        !t.feePcnt?.trim()
      );

      if (hasInvalidTariff) {
        return false;
      }

    }

    return true;
  };

  const isStoreValid = (node: TreeNodeModel) => {
    const data = node.entityData as any;

    if (!data) return false;

    // 🔥 validar límites de tienda
    if (data.adjustLimits === "Y" && hasInvalidLimits(data.storeLimits)) {
      return false;
    }

    if (!data.contractName.trim() || !data.mcc.trim() || !data.classifiers.corporateStatus.trim()) {

      return false;
    }

    let requiredFields = [
      "provinceId",
      "cantonCode",
      "districtCode",
      "postalCode",
      "firstName",
      "lastName",
      "phone",
      "mobilePhone",
      "email"
    ];

    if (data.paymentAddress.addressType == "PAYMENT_SCHEME_ADDRESS") {
      requiredFields.push("location");
      requiredFields.push("merchantName");
    } else {
      requiredFields.push("addressLine1");
    }

    for (const field of requiredFields) {
      const value = data.paymentAddress[field];
      if (
        value === undefined ||
        value === null ||
        value.toString().trim() === ""
      ) {
        return false;
        break;
      }
    }


    return true;
  };

  const validateTreeForApproval = (nodes: TreeNodeModel[]): boolean => {
    for (const node of nodes) {

      // 🔥 SOLO validar los nuevos
      if (node.isExistent !== true) {

        if (node.type === "STORE_CONTRACT") {
          if (!isStoreValid(node)) {
            setErrorMessage("La tienda tiene información inválida o incompleta.");
            return false;
          }
        }

        if (node.type === "DEVICE_CONTRACT") {
          if (!isDeviceValid(node)) {
            setErrorMessage("El dispositivo tiene información inválida o incompleta.");
            return false;
          }
        }
      }

      // 🔁 recursividad
      if (node.children?.length) {
        const valid = validateTreeForApproval(node.children);
        if (!valid) return false;
      }
    }

    return true;
  };

  const validateHasNews = (nodes: TreeNodeModel[]): boolean => {
    for (const node of nodes) {

      if (node.isExistent !== true) {
        if (
          node.type === "STORE_CONTRACT" ||
          node.type === "DEVICE_CONTRACT"
        ) {
          return true;
        }
      }

      if (node.children?.length) {
        const valid = validateHasNews(node.children);
        if (valid) return true;
      }
    }

    return false;
  };

  const findAncestorByType = (
    nodes: TreeNodeModel[],
    nodeId: string,
    type: EntityType
  ): TreeNodeModel | null => {

    let currentId = nodeId;

    while (true) {
      const parent = findParentNode(nodes, currentId);

      if (!parent) return null;

      if (parent.type === type) return parent;

      currentId = parent.id;
    }
  };

  const hasTerminal = (nodes: TreeNodeModel[]): boolean =>
    nodes.some(n =>
      (n.type === "DEVICE_CONTRACT" && n.saved) ||
      (n.children.length && hasTerminal(n.children))
    );

  const isTerminalComplete = (node: TreeNodeModel) =>
    node.entityData &&
    (node?.entityData as DeviceContract).product &&
    (node?.entityData as DeviceContract).contractName &&
    ((node?.entityData as DeviceContract).settlementParams?.localCurrencyAccountNumber || (node?.entityData as DeviceContract).settlementParams?.usdAccountNumber) &&
    (isUserPayfac || (node?.entityData as DeviceContract).settlementParams?.tariffPlan);

  const hasIncompleteTerminal = (nodes: TreeNodeModel[]): boolean =>
    nodes.some(n => {
      if (n.type === "DEVICE_CONTRACT" && n.isExistent == undefined && !isTerminalComplete(n)) {
        return true;
      }
      return n.children?.length ? hasIncompleteTerminal(n.children) : false;
    });

  const findParentNode = (
    nodes: TreeNodeModel[],
    childId: string
  ): TreeNodeModel | null => {
    for (const node of nodes) {
      if (node.children?.some(c => c.id === childId)) {
        return node;
      }

      const found = findParentNode(node.children || [], childId);
      if (found) return found;
    }

    return null;
  };

  let forceAdjustLimits = false;

  let isPayfacReq = false;

  if (selected && selected?.type === "DEVICE_CONTRACT") {
    const parentNode = findParentNode(tree, selected.id);

    const isStore = parentNode?.type === "STORE_CONTRACT";

    forceAdjustLimits =
      isStore &&
      ((parentNode?.entityData as any)?.adjustLimits === "Y");

    const divisionNode = findAncestorByType(tree, selected.id, "DIV_CONTRACT");

    isPayfacReq =
      (divisionNode?.entityData as any)?.egwPaymentFacilitator === "Y";
  }





  /* ===========================
     FORMS
     =========================== */

  const renderForm = () => {
    if (!selected) return null;

    const readOnlyTypes = [];

    const shouldBeReadOnly =
      (readOnlyTypes.includes(selected.type) && selected.isExistent === true);

    if (selected.isExistent === true) {
      selected.saved = true;
    }



    const props = {
      node: selected,
      onSave: saveNode,
      onChange: updateNodeLive,
      readOnly: shouldBeReadOnly
    };

    switch (selected.type) {
      case "CLIENT":
        return <ClientForm key={"CLIENT" + selected.id} {...props} />;

      case "CORP_CONTRACT":
        return <CorporationForm key={"CORP_CONTRACT" + selected.id} {...props} />;

      case "DIV_CONTRACT":
        return <DivisionForm key={"DIV_CONTRACT" + selected.id} {...props} />;

      case "STORE_CONTRACT":
        return <CompanyForm key={"STORE_CONTRACT" + selected.id} {...props} />;

      case "DEVICE_CONTRACT":
        return <TerminalForm key={"DEVICE_CONTRACT" + selected.id} {...props} forceAdjustLimits={forceAdjustLimits} isPayfacReq={isPayfacReq} filterPlans={true} />;

      default:
        return null;
    }
  };

  /* ===========================
     RENDER
     =========================== */

  return (
    <div className="container-fluid vh-100">



      <div className="row h-95">

        {
          isLoadingTree
            ? <Loading></Loading>
            : <>
              {/* ÁRBOL */}

              <div className="col-4 p-0 border-end d-flex flex-column">
                <div className="flex-grow-1 overflow-auto p-3">
                  <Tree
                    data={tree}
                    onSelect={setSelected}
                    onAdd={addNode}
                    onDelete={handleDelete}
                    isClientExistent={isClientExistent}
                  />

                  <hr />


                </div>
              </div>

              {/* FORM */}
              <div className="col-8 p-0 d-flex flex-column">
                <div className="flex-grow-1 overflow-auto p-4">
                  {renderForm()}
                </div>
              </div>
            </>
        }
      </div>

      <div className="d-flex justify-content-center gap-5 p-5">
        <button className="btn btn-secondary" onClick={handleBack}>
          Atrás
        </button>

        {(hasTerminal(tree) && !hasIncompleteTerminal(tree)) && (
          <button
            className="btn btn-success"
            onClick={handleToApprove}
            disabled={!hasTerminal(tree) || isSubmitting}
          >
            Enviar a aprobación
          </button>
        )}
      </div>

      {errorMessage &&
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }

      {successMessage &&
        <Message
          type="success"
          title="Exitoso"
          message={successMessage}
          closeDialog={removeSuccessMessage}
          handleOk={removeSuccessMessage}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }


      {confirmMessage &&
        <Message
          type="success"
          title="Confirmar"
          message={confirmMessage}
          closeDialog={removeConfirmMessage}
          showCancel={true}
          handleCancel={removeConfirmMessage}
          handleOk={confirmSendToApprove}
          okText="Confirmar"
          backgroundColor="rgba(0,0,0,0.4)"
        />
      }

    </div>
  );
};
